import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { analytics } from "@/lib/observability";

/**
 * Auto-track SPA page views with proper previous/current path semantics.
 * Mounted once in <App />.
 */
export function useTrackPageView(): void {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const lastDispatchedRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (currentPath === lastDispatchedRef.current) return;

    const previousPath = previousPathRef.current;

    // Defer one microtask + one rAF so Helmet has a chance to apply the title
    // and auth hydration has settled.
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        analytics.page({
          current_path: currentPath,
          previous_path: previousPath,
          title: typeof document !== "undefined" ? document.title : "",
        });
        // Sprint 3: route-aware replay gate + per-page UX state reset.
        try {
          analytics.onRouteChange(location.pathname);
        } catch { /* noop */ }
        import("@/lib/observability/ux")
          .then((m) => m.notifyUxRouteChange(location.pathname))
          .catch(() => undefined);
      });
    });

    previousPathRef.current = currentPath;
    lastDispatchedRef.current = currentPath;
  }, [location]);
}
